import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { StateManagementService, BrevoConfig } from '../../services/state-management.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-status-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDividerModule,
  ],
  templateUrl: './status-dashboard.component.html',
  styleUrl: './status-dashboard.component.scss',
})
export class StatusDashboardComponent implements OnInit {
  @Input() organizationId!: string;
  @Output() onboardingComplete = new EventEmitter<void>();

  brevoForm!: FormGroup;
  brevoLoading = false;
  brevoConfigured = false;
  brevoAccountEmail = '';
  showApiKey = false;
  isRefreshing = false;
  provisioningStatus: string | null = null;

  onboardingStatus: any = null;

  constructor(
    private fb: FormBuilder,
    public stateService: StateManagementService,
    private api: OnboardingApiService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const state = this.stateService.snapshot;
    this.brevoConfigured = state.brevoConfigured;
    this.provisioningStatus = state.provisioningStatus;

    this.brevoForm = this.fb.group({
      apiKey: ['', Validators.required],
      senderName: [state.organization?.Name || '', Validators.required],
      senderEmail: [state.organization?.Email || '', [Validators.required, Validators.email]],
    });

    this.loadStatus();
  }

  async loadStatus(): Promise<void> {
    this.isRefreshing = true;
    try {
      const status = await this.api.getOnboardingStatus(this.organizationId).toPromise();
      this.onboardingStatus = status;
      this.brevoConfigured = !!status?.brevoConfigured;
      this.provisioningStatus = status?.provisioningStatus || this.provisioningStatus;

      if (status?.isComplete) {
        const wasAlreadyComplete = this.stateService.snapshot.isComplete;
        this.stateService.checkAndSetComplete();

        if (!wasAlreadyComplete) {
          // Send immediate confirmation email (thank you + workspace being set up)
          this.api.sendOnboardingCompleteEmail(this.organizationId)
            .subscribe({
              next: () => console.log('Onboarding confirmation email sent'),
              error: (err) => console.warn('Confirmation email failed (non-critical):', err),
            });

          // Queue spoke provisioning (sends console-ready email with password reset after completion)
          this.queueSpokeProvisioning();

          this.snackBar.open(
            'Onboarding complete! You will receive a confirmation email shortly. A second email with your console access link will follow once your workspace is ready.',
            'Close', { duration: 8000 }
          );
        }

        this.onboardingComplete.emit();
      }
    } catch (err: any) {
      // status endpoint might not be deployed yet — fail silently
    } finally {
      this.isRefreshing = false;
    }
  }

  private queueSpokeProvisioning(): void {
    // Skip if already queued or complete
    if (this.provisioningStatus === 'queued' || this.provisioningStatus === 'complete') return;

    this.api.initiateSpokeProvisioning(this.organizationId)
      .subscribe({
        next: (result) => {
          this.provisioningStatus = result.status;
          this.stateService.setProvisioningStatus(result.status);
          if (result.status === 'provisioning_queued') {
            this.snackBar.open(
              'Your organization workspace is being set up!', 'Close', { duration: 5000 }
            );
          }
        },
        error: (err) => console.warn('Provisioning queue failed (non-critical):', err),
      });
  }

  async saveBrevoConfig(): Promise<void> {
    if (this.brevoForm.invalid) {
      this.brevoForm.markAllAsTouched();
      return;
    }
    this.brevoLoading = true;
    const v = this.brevoForm.value;

    try {
      const result = await this.api.saveBrevoConfig(
        this.organizationId,
        v.apiKey,
        v.senderName,
        v.senderEmail,
      ).toPromise();

      const config: BrevoConfig = {
        apiKey: v.apiKey,
        senderName: v.senderName,
        senderEmail: v.senderEmail,
      };

      this.stateService.setBrevoConfigured(config);
      this.brevoConfigured = true;
      this.brevoAccountEmail = result?.accountEmail || '';
      this.brevoForm.get('apiKey')?.setValue(''); // clear key from UI

      this.stateService.checkAndSetComplete();
      this.snackBar.open('Brevo configured successfully!', 'Close', { duration: 3000 });

      // Reload status — may detect isComplete for the first time
      await this.loadStatus();
    } catch (err: any) {
      this.snackBar.open('Error: ' + (err.message || err), 'Close', { duration: 5000 });
    } finally {
      this.brevoLoading = false;
    }
  }

  get steps() {
    return this.onboardingStatus?.steps || {
      businessDetails: !!this.stateService.snapshot.businessDetails,
      representativeDetails: !!this.stateService.snapshot.representativeDetails,
      documents: this.stateService.snapshot.documentsComplete,
      stripeConnect: this.stateService.snapshot.stripeStatus === 'active',
      brevo: this.brevoConfigured,
    };
  }

  get isComplete(): boolean {
    const s = this.steps;
    return s.stripeConnect && s.brevo;
  }

  get completedCount(): number {
    return Object.values(this.steps).filter(Boolean).length;
  }

  get totalSteps(): number {
    return Object.keys(this.steps).length;
  }

  get provisioningLabel(): string {
    const labels: Record<string, string> = {
      queued: 'Organization workspace setup queued',
      in_progress: 'Setting up your organization workspace...',
      complete: 'Organization workspace is live',
      already_provisioned: 'Organization workspace is live',
      already_queued: 'Organization workspace setup queued',
    };
    return labels[this.provisioningStatus || ''] || '';
  }
}
